import {
  casesCollection,
  reviewsCollection,
  doc,
  addDoc,
  setDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from "./db/firebase.js";

import { setupAuth, isAdmin } from "./auth/auth.js";

import { createCaseCard } from "./cases/cases.js";
import { createReviewCard } from "./reviews/reviews.js";
import "./admin-login.js";

// Элементы UI
const practiceGrid = document.getElementById("practice-grid");
const practiceLoading = document.getElementById("practice-loading");
const reviewsGrid = document.getElementById("reviews-grid");
const reviewsLoading = document.getElementById("reviews-loading");

const formModal = document.getElementById("form-modal");
const detailModal = document.getElementById("detail-modal");
const dataForm = document.getElementById("data-form");

// Модальное окно подтверждения
const confirmModal = document.getElementById("confirm-modal");
const confirmModalTitle = document.getElementById("confirm-modal-title");
const confirmModalMessage = document.getElementById("confirm-modal-message");
const confirmModalOk = document.getElementById("confirm-modal-ok");
let onConfirmCallback = null;

/**
 * Получает метку времени для сортировки.
 * ВАЖНО: При ожидающей локальной записи (pending write) возвращает MAX_SAFE_INTEGER,
 * чтобы документ всегда сортировался как самый НОВЫЙ, предотвращая "прыжки".
 * @param {import("firebase/firestore").DocumentSnapshot} doc - полный документ-снэпшот Firestore.
 * @returns {number} Метка времени в миллисекундах или MAX_SAFE_INTEGER.
 */
function getSortableTimestamp(doc) {
  const data = doc.data();
  const timestamp = data.createdAt;

  // 1. Если у нас есть финальная серверная метка времени, используем ее.
  if (timestamp && typeof timestamp.toMillis === "function") {
    return timestamp.toMillis();
  }

  // 2. Если это ожидающая локальная запись, считаем ее самой новой.
  // Это гарантирует, что она будет в конце (для asc) или в начале (для desc),
  // и не "прыгнет" с позиции 0 (самой старой) при получении реальной метки.
  if (doc.metadata.hasPendingWrites) {
    return Number.MAX_SAFE_INTEGER;
  }

  // 3. Fallback (для документов без createdAt, что нежелательно)
  return 0;
}

try {
  // 1. Аутентификация
  setupAuth();
} catch (error) {
  console.error("Ошибка инициализации Firebase:", error);
  practiceLoading.innerText =
    "Ошибка загрузки данных. Пожалуйста, проверьте консоль.";
  reviewsLoading.innerText = "Ошибка загрузки данных.";
}

// 2. ЛОГИКА "ПРАКТИКИ" (КЕЙСЫ) - Сортировка по возрастанию (старые сверху)
function loadCases() {
  const q = query(casesCollection);
  onSnapshot(
    q,
    (snapshot) => {
      // Используем полный doc snapshot для доступа к metadata
      practiceGrid.innerHTML = ""; // <-- always clear first

      if (snapshot.empty) {
        // Show styled "no cases yet" message
        const emptyMsg = document.createElement("p");
        emptyMsg.className =
          "w-full col-span-full block text-center text-slate-500 text-lg py-12 border border-dashed border-slate-300 rounded-lg";
        emptyMsg.innerText = "Успешных кейсов пока нет.";
        practiceGrid.appendChild(emptyMsg);
        practiceLoading.style.display = "none";
        return;
      }

      practiceLoading.style.display = "none";

      const docs = snapshot.docs;

      // Используем полный doc snapshot для доступа к metadata
      // const docs = snapshot.docs;

      // Сортировка: Старые сверху (1, 2, 3) -> A - B (Ascending)
      docs.sort((a, b) => getSortableTimestamp(a) - getSortableTimestamp(b));

      const fragment = document.createDocumentFragment();
      docs.forEach((doc) => {
        fragment.appendChild(
          createCaseCard(
            doc.data(),
            doc.id,
            isAdmin,
            openFormModal,
            showDetailModal,
            showConfirm,
          ),
        );
      });

      // Обновляем DOM один раз
      practiceGrid.innerHTML = "";
      practiceGrid.appendChild(fragment);

      lucide.createIcons();
    },
    (error) => {
      /* console.error("Ошибка загрузки кейсов:", error);
            practiceGrid.innerHTML = "";
            practiceLoading.style.display = "block";
            practiceLoading.innerText = "Не удалось загрузить кейсы."; */
      console.error("Ошибка загрузки кейсов:", error);
      const errorMsg = document.createElement("p");
      errorMsg.className =
        "text-center text-red-500 text-lg py-12 border border-dashed border-red-300 rounded-lg";
      errorMsg.innerText = "Не удалось загрузить кейсы.";
      practiceGrid.innerHTML = "";
      practiceGrid.appendChild(errorMsg);
    },
  );
}

// Показать модальное окно с деталями кейса
function showDetailModal(data) {
  document.getElementById("detail-modal-title").innerText = data.title;
  document.getElementById("detail-modal-text").innerText = data.details;
  detailModal.style.display = "flex";
}

// 3. ЛОГИКА "ОТЗЫВОВ" - Сортировка по убыванию (новые сверху)
function loadReviews() {
  const q = isAdmin
    ? query(reviewsCollection) // админ видит все
    : query(reviewsCollection, where("approved", "==", true)); // пользователи только одобренные

  onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        reviewsGrid.innerHTML = "";
        const emptyMsg = document.createElement("p");
        emptyMsg.className =
          "w-full col-span-full text-center text-slate-500 text-lg py-12 border border-dashed border-slate-300 rounded-lg";
        emptyMsg.innerText = "Отзывов пока нет. Будьте первыми!";
        reviewsGrid.appendChild(emptyMsg);
        reviewsLoading.style.display = "none";
        return;
      }

      reviewsLoading.style.display = "none";

      const docs = snapshot.docs;

      // Сортировка: новые сверху
      docs.sort((a, b) => getSortableTimestamp(b) - getSortableTimestamp(a));

      const fragment = document.createDocumentFragment();
      docs.forEach((doc) => {
        fragment.appendChild(
          createReviewCard(
            doc.data(),
            doc.id,
            isAdmin,
            openFormModal,
            showConfirm,
          ),
        );
      });

      reviewsGrid.innerHTML = "";
      reviewsGrid.appendChild(fragment);

      lucide.createIcons();
    },
    (error) => {
      console.error("Ошибка загрузки отзывов:", error);
      reviewsLoading.style.display = "block";
      reviewsLoading.innerText = "Не удалось загрузить отзывы.";
    },
  );
}

setupAuth(loadCases, loadReviews);

// 4. ОБЩАЯ ЛОГИКА ФОРМ (для Кейсов и Отзывов)
function openFormModal(mode, data = {}, id = null) {
  dataForm.reset(); // Сбрасываем форму
  document.getElementById("form-mode").value = mode;
  document.getElementById("edit-id").value = id || "";

  if (mode === "case") {
    document.getElementById("form-modal-title").innerText = id
      ? "Редактировать кейс"
      : "Добавить кейс";
    document
      .querySelectorAll(".form-field-case")
      .forEach((el) => (el.style.display = "block"));
    document
      .querySelectorAll(".form-field-review")
      .forEach((el) => (el.style.display = "none"));

    document.getElementById("form-title").value = data.title || "";
    document.getElementById("form-summary").value = data.summary || "";
    document.getElementById("form-details").value = data.details || "";
  } else if (mode === "review") {
    document.getElementById("form-modal-title").innerText = id
      ? "Редактировать отзыв"
      : "Оставить отзыв";
    document
      .querySelectorAll(".form-field-case")
      .forEach((el) => (el.style.display = "none"));
    document
      .querySelectorAll(".form-field-review")
      .forEach((el) => (el.style.display = "block"));

    document.getElementById("form-name").value = data.name || "";
    document.getElementById("form-review-text").value = data.text || "";
  }

  formModal.style.display = "flex";
}

function closeFormModal() {
  formModal.style.display = "none";
}

// Сохранение данных
dataForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  const mode = document.getElementById("form-mode").value;
  const id = document.getElementById("edit-id").value;

  const submitButton = dataForm.querySelector('button[type="submit"]');
  const errorBlock = document.getElementById("form-error"); // блок для ошибок

  let data;
  let collectionRef;

  // Очистка ошибок перед началом
  errorBlock.textContent = "";
  errorBlock.classList.add("hidden");

  // Блокируем кнопку
  submitButton.disabled = true;
  submitButton.innerText = "Сохранение...";

  try {
    if (mode === "case") {
      if (!isAdmin) throw new Error("Нет прав для этого действия");

      data = {
        title: document.getElementById("form-title").value,
        summary: document.getElementById("form-summary").value,
        details: document.getElementById("form-details").value,
        updatedAt: serverTimestamp(),
      };

      if (!id) data.createdAt = serverTimestamp();
      collectionRef = casesCollection;
    } else if (mode === "review") {
      const name = document.getElementById("form-name").value.trim();
      const text = document.getElementById("form-review-text").value.trim();

      // ✅ Валидация обязательных полей
      // if (!name) {
      //   errorBlock.textContent = "Пожалуйста, укажите ваше имя.";
      //   errorBlock.classList.remove("hidden");
      //   return;
      // }
      // if (!text) {
      //   errorBlock.textContent = "Пожалуйста, напишите текст отзыва.";
      //   errorBlock.classList.remove("hidden");
      //   return;
      // }
      if (!name) {
        errorBlock.textContent = "Пожалуйста, укажите ваше имя.";
        errorBlock.classList.remove("hidden");
        submitButton.disabled = false;
        submitButton.innerText = "Сохранить";
        return;
      }
      if (!text) {
        errorBlock.textContent = "Пожалуйста, напишите текст отзыва.";
        errorBlock.classList.remove("hidden");
        submitButton.disabled = false;
        submitButton.innerText = "Сохранить";
        return;
      }

      const isApproved = isAdmin ? true : false;

      data = {
        name: name,
        text: text,
        approved: isApproved,
        notified: false,
        updatedAt: serverTimestamp(),
      };

      if (!id) data.createdAt = serverTimestamp();
      collectionRef = reviewsCollection;
    }

    // 🔹 Сохраняем документ
    if (id) {
      await setDoc(doc(collectionRef, id), data, { merge: true });
      console.log(`Документ ${id} обновлен.`);
    } else {
      await addDoc(collectionRef, data);
      console.log("Новый документ добавлен.");

      if (mode === "review" && !isAdmin) {
        showThankYouModal();
      }
    }

    closeFormModal();
  } catch (error) {
    console.error("Ошибка сохранения данных:", error);
    errorBlock.textContent =
      "Произошла ошибка при сохранении. Попробуйте позже.";
    errorBlock.classList.remove("hidden");
  } finally {
    // Разблокируем кнопку
    submitButton.disabled = false;
    submitButton.innerText = "Сохранить";
  }
});

// dataForm.addEventListener("submit", async (e) => {
//   e.preventDefault();
//   const mode = document.getElementById("form-mode").value;
//   const id = document.getElementById("edit-id").value;

//   const submitButton = dataForm.querySelector('button[type="submit"]');

//   let data;
//   let collectionRef;

//   try {
//     // Блокируем кнопку
//     submitButton.disabled = true;
//     submitButton.innerText = "Сохранение...";

//     if (mode === "case") {
//       if (!isAdmin) throw new Error("Нет прав для этого действия");
//       data = {
//         title: document.getElementById("form-title").value,
//         summary: document.getElementById("form-summary").value,
//         details: document.getElementById("form-details").value,
//         updatedAt: serverTimestamp(),
//       };
//       // Добавляем createdAt только при создании
//       if (!id) data.createdAt = serverTimestamp();

//       collectionRef = casesCollection;
//     } else if (mode === "review") {
//       const isApproved = isAdmin ? true : false; // <-- ADMIN approved automatically
//       data = {
//         name: document.getElementById("form-name").value || "Аноним",
//         text: document.getElementById("form-review-text").value,
//         approved: isApproved, // <-- important!
//         notified: false,
//         updatedAt: serverTimestamp(),
//       };
//       collectionRef = reviewsCollection;
//       console.log("Сохраняем данные:", data);

//       if (!id) data.createdAt = serverTimestamp();
//     }

//     if (id) {
//       // Обновление
//       await setDoc(doc(collectionRef, id), data, { merge: true });
//       console.log(`Документ ${id} обновлен.`);
//     } else {
//       // Создание
//       await addDoc(collectionRef, data);
//       console.log("Новый документ добавлен.");

//       // 🌟 Show thank-you modal for regular users
//       if (mode === "review" && !isAdmin) {
//         showThankYouModal();
//       }
//     }

//     closeFormModal();
//   } catch (error) {
//     console.error("Ошибка сохранения данных:", error);
//   } finally {
//     // Разблокируем кнопку
//     submitButton.disabled = false;
//     submitButton.innerText = "Сохранить";
//   }
// });

// 5. УВЕДОМЛЕНИЯ И МОДАЛЬНЫЕ ОКНА
function showThankYouModal() {
  const modal = document.createElement("div");
  modal.className =
    "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50";

  modal.innerHTML = `
          <div class="
            bg-white 
            p-6 sm:p-8        /* smaller padding on tiny screens */
            rounded-lg 
            max-w-xs sm:max-w-sm  /* max width adjusts on small screens */
            w-full
            text-center 
            shadow-lg
          ">
            <h3 class="text-lg sm:text-xl font-bold text-orange-900 mb-4">
              Спасибо за отзыв!
            </h3>
            <p class="text-sm sm:text-base text-slate-700 mb-6">
              После модерации он появится в разделе "Отзывы клиентов".
            </p>
            <button class="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600">
              Закрыть
            </button>
          </div>
        `;

  document.body.appendChild(modal);

  const removeModal = () => modal.remove();
  modal.querySelector("button").addEventListener("click", removeModal);
}

// Модальное окно подтверждения (Confirm)
function showConfirm(title, message, onConfirm) {
  confirmModalTitle.innerText = title;
  confirmModalMessage.innerText = message;
  onConfirmCallback = onConfirm;
  confirmModal.style.display = "flex";
  lucide.createIcons();
}

function hideConfirm() {
  confirmModal.style.display = "none";
  onConfirmCallback = null;
}

document.getElementById("confirm-modal-ok").addEventListener("click", () => {
  if (onConfirmCallback) {
    onConfirmCallback();
  }
  hideConfirm();
});
document
  .getElementById("confirm-modal-cancel")
  .addEventListener("click", hideConfirm);

// --- ЛОГИКА ЗАКРЫТИЯ МОДАЛЬНЫХ ОКОН ---

// Закрытие по кнопкам
document
  .getElementById("form-modal-close")
  .addEventListener("click", closeFormModal);
document
  .getElementById("form-modal-cancel")
  .addEventListener("click", closeFormModal);
document
  .getElementById("detail-modal-close")
  .addEventListener("click", () => (detailModal.style.display = "none"));

// Закрытие по клику на фон (Overlay)
// formModal исключен, чтобы предотвратить случайную потерю данных.
[detailModal, confirmModal].forEach((modal) => {
  modal.addEventListener("click", (e) => {
    // Закрываем только если клик был непосредственно по модальному оверлею (т.е., вне контента)
    if (e.target === modal) {
      modal.style.display = "none";
      if (modal === confirmModal) {
        onConfirmCallback = null;
      }
    }
  });
});

// Защита от всплытия кликов внутри контента
document
  .getElementById("form-modal-content")
  .addEventListener("click", (e) => e.stopPropagation());
document
  .getElementById("detail-modal-content")
  .addEventListener("click", (e) => e.stopPropagation());
document
  .getElementById("confirm-modal-content")
  .addEventListener("click", (e) => e.stopPropagation());

// 6. ОБЩИЕ СКРИПТЫ САЙТА

// Текущий год в футере
document.getElementById("current-year").innerText = new Date().getFullYear();

// Мобильное меню
const mobileMenuBtn = document.getElementById("mobile-menu-btn");
const mobileMenu = document.getElementById("mobile-menu");
const mobileMenuClose = document.getElementById("mobile-menu-close");

function openMenu() {
  mobileMenu.classList.remove("hidden");
  document.body.style.overflow = "hidden"; // блокируем прокрутку
}

function closeMenu() {
  mobileMenu.classList.add("hidden");
  document.body.style.overflow = ""; // разблокируем прокрутку
}

mobileMenuBtn.addEventListener("click", openMenu);
mobileMenuClose.addEventListener("click", closeMenu);

// Закрывать при клике на пункт меню
mobileMenu.querySelectorAll("a").forEach((link) => {
  link.addEventListener("click", closeMenu);
});

// Плавная прокрутка
document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
  anchor.addEventListener("click", function (e) {
    e.preventDefault();
    const targetId = this.getAttribute("href");
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
      });
    }
  });
});

// Кнопки открытия форм
document
  .getElementById("add-case-btn")
  .addEventListener("click", () => openFormModal("case"));
document
  .getElementById("add-review-btn")
  .addEventListener("click", () => openFormModal("review"));

// Инициализация иконок
lucide.createIcons();
